import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import { api, formatApiDetail } from "../../lib/api";
import {
  blobLooksLikeApiError,
  readApiErrorDetail,
  resolveUploadedContentType,
  shouldDownloadUploadedFile,
  downloadFilenameFromHeaders,
  isMobilePdfViewer,
  pdfViewerSrc,
} from "../../lib/portalFiles";

export default function FileViewPage() {
  const { fileId, "*": fileNameParam } = useParams();
  const displayName = useMemo(() => {
    if (!fileNameParam) return "File";
    try {
      return decodeURIComponent(fileNameParam);
    } catch {
      return fileNameParam;
    }
  }, [fileNameParam]);

  const [viewerSrc, setViewerSrc] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [mobilePdf, setMobilePdf] = useState(false);
  const [downloadName, setDownloadName] = useState(displayName);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!fileId) {
      setError("File not found");
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        // Single authenticated fetch (cookie + Bearer). Avoid cookie-only HEAD —
        // new tabs / cross-site gaps used to 401 and bounce to login.
        const { data, headers } = await api.get(`/portal/files/item/${fileId}/download`, {
          responseType: "blob",
        });
        if (await blobLooksLikeApiError(data)) {
          throw new Error("Download failed");
        }
        if (cancelled) return;

        const resolvedName = downloadFilenameFromHeaders(headers, displayName);
        const fileMeta = {
          name: resolvedName,
          original_filename: resolvedName,
        };
        const mime = resolveUploadedContentType(fileMeta, headers["content-type"]);

        if (shouldDownloadUploadedFile(fileMeta, mime)) {
          const blobUrl = URL.createObjectURL(new Blob([data], { type: mime }));
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = resolvedName;
          link.rel = "noopener";
          link.click();
          URL.revokeObjectURL(blobUrl);
          window.setTimeout(() => window.close(), 250);
          return;
        }

        setDownloadName(resolvedName);
        const mimeLower = String(mime || "").toLowerCase();
        const pdf = mimeLower === "application/pdf" || /\.pdf$/i.test(resolvedName);
        const mobile = pdf && isMobilePdfViewer();
        setIsPdf(pdf);
        setMobilePdf(mobile);

        const blobUrl = URL.createObjectURL(new Blob([data], { type: mime }));
        blobUrlRef.current = blobUrl;
        if (!cancelled) {
          if (pdf) {
            setViewerSrc(pdfViewerSrc(blobUrl, { fitWidth: !mobile }));
          } else {
            setViewerSrc(blobUrl);
          }
        }
      } catch (err) {
        if (!cancelled) {
          const raw = await readApiErrorDetail(err);
          setError(formatApiDetail(raw, "Could not open file"));
        }
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [displayName, fileId]);

  useEffect(() => {
    document.title = downloadName ? `${downloadName} | TAĪSTU` : "TAĪSTU | Client Portal";
  }, [downloadName]);

  const handleDownload = useCallback(async () => {
    if (!fileId || downloading) return;
    setDownloading(true);
    try {
      const { data, headers } = await api.get(`/portal/files/item/${fileId}/download`, {
        responseType: "blob",
      });
      if (await blobLooksLikeApiError(data)) {
        throw new Error("Download failed");
      }
      const resolvedName = downloadFilenameFromHeaders(headers, displayName);
      const fileMeta = {
        name: resolvedName,
        original_filename: resolvedName,
      };
      const mime = resolveUploadedContentType(fileMeta, headers["content-type"]);
      const blobUrl = URL.createObjectURL(new Blob([data], { type: mime }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedName;
      link.rel = "noopener";
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const raw = await readApiErrorDetail(err);
      setError(formatApiDetail(raw, "Could not download file"));
    } finally {
      setDownloading(false);
    }
  }, [displayName, downloading, fileId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 t-meta">
        {error}
      </div>
    );
  }

  if (!viewerSrc) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 t-meta">
        Opening file…
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] min-h-[100svh] w-full max-w-[100vw] flex-col overflow-hidden bg-white">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--t-border)] px-4 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-light text-[var(--t-text)]" title={downloadName}>
            {downloadName}
          </div>
          {mobilePdf && isPdf ? (
            <p className="mt-0.5 text-[10px] text-[var(--t-meta)]">Swipe up to see all pages</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="t-btn-ghost inline-flex shrink-0 items-center gap-2 px-3 py-2 text-xs disabled:opacity-50"
          data-testid="file-view-download"
        >
          <Download size={14} strokeWidth={1.5} aria-hidden />
          {downloading ? "Downloading…" : isPdf ? "Download PDF" : "Download"}
        </button>
      </header>
      <div className="min-h-0 flex-1">
        <iframe
          title={downloadName}
          src={viewerSrc}
          className="h-full min-h-0 w-full border-0 bg-white"
        />
      </div>
    </div>
  );
}
